/**
 * PCO Services API Types
 * API Base: https://api.planningcenteronline.com/services/v2
 */

export interface PcoServiceType {
  id: string;
  type: 'ServiceType';
  attributes: {
    archived_at?: string | null;
    created_at?: string | null;
    name: string;
    sequence?: number | null;
    updated_at?: string | null;
  };
  relationships?: {
    parent?: {
      data: { type: 'Folder'; id: string } | null;
    };
  };
}

export interface PcoServiceFolder {
  id: string;
  type: 'Folder';
  attributes: {
    container?: string | null;
    created_at?: string | null;
    name: string;
    updated_at?: string | null;
  };
  relationships?: {
    campus?: {
      data: { type: 'Campus'; id: string } | null;
    };
    parent?: {
      data: { type: 'Folder'; id: string } | null;
    };
  };
}

export interface PcoServiceTeam {
  id: string;
  type: 'Team';
  attributes: {
    archived_at?: string | null;
    created_at?: string | null;
    name: string;
    sequence?: number | null;
    updated_at?: string | null;
  };
  relationships?: {
    service_type?: {
      data: { type: 'ServiceType'; id: string } | null;
    };
  };
}

export interface PcoServicePersonLike {
  id: string;
  type: string;
  attributes?: {
    email_address?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    name?: string | null;
  };
  relationships?: {
    person?: {
      data: { type: 'Person'; id: string } | null;
    };
  };
}

export interface PcoServicesApiResponse<T> {
  data: T;
  included?: unknown[];
  links?: {
    self?: string;
    next?: string;
    prev?: string;
  };
  meta?: Record<string, unknown>;
}

/** One Services team lead row: PCO person is stable; MHX user id is filled when linked. */
export interface PcoServiceTeamLeaderRef {
  pco_person_id: string;
  mhx_user_id?: string | null;
}

export interface PcoServiceTeamAccessShape {
  pco_id: string;
  pco_service_type_id: string | null;
  /** Optional manual campus override inherited from the service type. */
  mhx_campus_ids?: string[];
  /** PCO campus ids resolved automatically through the service type's folder. */
  pco_campus_ids?: string[];
  /** MinistryHub campus ids resolved from pco_campus_ids during sync. */
  mhx_auto_campus_ids?: string[];
  mhx_team_leaders?: PcoServiceTeamLeaderRef[];
}

export function pcoServiceTeamLeaderMhxUserIds(
  team: PcoServiceTeamAccessShape,
): string[] {
  return [
    ...new Set(
      (team.mhx_team_leaders ?? [])
        .map((leader) => leader.mhx_user_id)
        .filter((id): id is string => Boolean(id?.trim())),
    ),
  ];
}

/** Manual campus mappings override the MinistryHub campus inferred from PCO. */
export function pcoServiceTeamMatchesCampusAccess(
  team: Pick<
    PcoServiceTeamAccessShape,
    'mhx_campus_ids' | 'mhx_auto_campus_ids'
  >,
  campusId: string,
): boolean {
  if (Array.isArray(team.mhx_campus_ids)) {
    return team.mhx_campus_ids.includes(campusId);
  }

  return (team.mhx_auto_campus_ids ?? []).includes(campusId);
}

export interface PcoServiceTypeDocument {
  _id?: unknown;
  pco_id: string;
  tenant_id?: string;
  name: string;
  /** Optional manual override; when absent, mhx_auto_campus_ids is used. */
  mhx_campus_ids?: string[];
  /** Parent Services folder reported by PCO. */
  pco_parent_folder_id?: string | null;
  /** PCO campus ids resolved automatically through the parent folder. */
  pco_campus_ids?: string[];
  /** MinistryHub campus ids matched from PCO campus names during sync. */
  mhx_auto_campus_ids?: string[];
  sequence: number;
  archived_at: Date | null;
  pco_created_at: Date;
  pco_updated_at: Date;
  synced_at: Date;
  created_at: Date;
  updated_at: Date;
}

export interface PcoServiceTeamDocument {
  _id?: unknown;
  pco_id: string;
  tenant_id?: string;
  pco_service_type_id: string | null;
  name: string;
  sequence: number;
  archived_at: Date | null;
  pco_created_at: Date;
  pco_updated_at: Date;
  synced_at: Date;
  created_at: Date;
  updated_at: Date;
  mhx_team_leaders?: PcoServiceTeamLeaderRef[];
}

export interface PcoServiceTeamMembershipDocument {
  _id?: unknown;
  pco_id: string;
  tenant_id?: string;
  pco_team_id: string;
  pco_service_type_id: string | null;
  pco_person_id: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  synced_at: Date;
  created_at: Date;
  updated_at: Date;
}

export interface ServicesSyncResult {
  success: boolean;
  serviceTypes: {
    total: number;
    synced: number;
  };
  teams: {
    total: number;
    synced: number;
  };
  teamLeaders: {
    total: number;
    synced: number;
    linked: number;
  };
  memberships: {
    total: number;
    synced: number;
  };
  errors: string[];
  duration: number;
}
